import { useCallback, useMemo, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useWorkspaceStore } from '@/stores/workspace';
import { FileNode } from '@/pages/workspace/types';
import { useWorkspaceState } from './useWorkspaceState';
import { useDebounce } from '../use-debounce';

export const useFileSelection = () => {
  const { isStateLoaded } = useWorkspaceState();
  const {
    instructions,
    fileTree,
    userSelectedFiles,
    setUserSelectedFiles,
    contextSelectedFiles,
    setContextSelectedFiles,
    isAutoContextEnabled,
    tokenBudget,
    findRelevantFiles,
    findRelevantFilesFromInstructions,
    cachedInstructionContext,
    setCachedInstructionContext,
  } = useWorkspaceStore(state => ({
    instructions: state.instructions,
    fileTree: state.fileTree,
    userSelectedFiles: state.userSelectedFiles,
    setUserSelectedFiles: state.setUserSelectedFiles,
    contextSelectedFiles: state.contextSelectedFiles,
    setContextSelectedFiles: state.setContextSelectedFiles,
    isAutoContextEnabled: state.isAutoContextEnabled,
    tokenBudget: state.tokenBudget,
    findRelevantFiles: state.findRelevantFiles,
    findRelevantFilesFromInstructions: state.findRelevantFilesFromInstructions,
    cachedInstructionContext: state.cachedInstructionContext,
    setCachedInstructionContext: state.setCachedInstructionContext,
  }));
  const debouncedInstructions = useDebounce(instructions, 500);
  const requestAbortControllerRef = useRef<AbortController | null>(null);

  const getAllDescendantFiles = useCallback((node: FileNode, files: Set<string>, includeIgnored = false) => {
    if (node.type === 'file' && node.tokenizable) {
      if (includeIgnored || !node.ignored) {
        files.add(node.path);
      }
    }
    node.children?.forEach((child) => getAllDescendantFiles(child, files, includeIgnored));
  }, []);

  // Reconcile selected files with current file tree
  useEffect(() => {
    // Don't run reconciliation until both the state is loaded and a non-empty file tree is present.
    if (!isStateLoaded || fileTree.length === 0) return;

    const allFilePaths = new Set<string>();
    const traverse = (nodes: FileNode[]) => {
      for (const node of nodes) {
        if (node.type === 'file') {
          allFilePaths.add(node.path);
      }
        if (node.children) { 
          traverse(node.children);
        }
      }
    };
    traverse(fileTree);

    const filesToKeep = new Set<string>();
    for (const file of userSelectedFiles) {
      if (allFilePaths.has(file)) {
        filesToKeep.add(file);
      }
    }

    // Only update state if there is a difference to prevent infinite loops.
    if (filesToKeep.size !== userSelectedFiles.size) {
      setUserSelectedFiles(() => filesToKeep);
    }
  }, [fileTree, userSelectedFiles, isStateLoaded, setUserSelectedFiles]);

  const toggleNodeSelection = useCallback((node: FileNode, checked: boolean) => {
    const filesToToggle = new Set<string>();
    if (node.type === 'file') {
      if (node.tokenizable) {
        filesToToggle.add(node.path);
      }
    } else {
      // When unchecking a directory, we unselect everything, even individually selected ignored files.
      // When checking, we only select non-ignored files.
      getAllDescendantFiles(node, filesToToggle, !checked);
    }

    setUserSelectedFiles(prev => {
      const next = new Set(prev);
      if (checked) {
        filesToToggle.forEach((file) => next.add(file));
      } else {
        filesToToggle.forEach((file) => next.delete(file));
      }
      return next;
    });
  }, [getAllDescendantFiles, setUserSelectedFiles]);

  const prevTokenBudgetRef = useRef(tokenBudget);

  useEffect(() => {
    if (!isStateLoaded) return;

    const budgetChanged = prevTokenBudgetRef.current !== tokenBudget;
    prevTokenBudgetRef.current = tokenBudget;

    // Abort any ongoing request when dependencies change.
    if (requestAbortControllerRef.current) {
      requestAbortControllerRef.current.abort();
    }
    const controller = new AbortController();
    requestAbortControllerRef.current = controller;

    const recomputeContext = async () => {
      if (!isAutoContextEnabled) {
        setContextSelectedFiles(new Map());
        return;
      }

      if (userSelectedFiles.size > 0) {
        const fileNodeMap = new Map<string, FileNode>();
        const traverse = (nodes: FileNode[]) => {
          for (const node of nodes) {
            fileNodeMap.set(node.path, node);
            if (node.children) traverse(node.children);
          }
        };
        traverse(fileTree);
        
        const seedFiles = Array.from(userSelectedFiles).filter(path => !fileTree.find(n => n.path === path)?.ignored);

        let toastId: string | number | undefined;
        if (budgetChanged) {
          toastId = toast.loading('Re-calculating relevant files with new budget...');
        }
        const relevantFiles = await findRelevantFiles({ seedFiles, tokenBudget });

        if (controller.signal.aborted) return;

        if (toastId) toast.dismiss(toastId);

        if (relevantFiles) {
          const alreadySelected = new Set(seedFiles);
          const newContextFiles = new Set(relevantFiles.filter((p) => !alreadySelected.has(p)));
          if (newContextFiles.size > 0) {
            setContextSelectedFiles(new Map([['auto-context', newContextFiles]]));
            if (budgetChanged) toast.success(`Found ${newContextFiles.size} relevant files with new budget.`);
          } else {
            setContextSelectedFiles(new Map());
            if (budgetChanged) toast.info('No additional relevant files found with new budget.');
          }
        } else if (relevantFiles === null) {
          setContextSelectedFiles(new Map());
        }
      } else if (debouncedInstructions.trim().length >= 20) { // Only search from instructions if some are provided
        if (cachedInstructionContext && cachedInstructionContext.size > 0 && !budgetChanged) {
          setContextSelectedFiles(new Map([['auto-context', cachedInstructionContext]]));
          return;
        }
      
        const relevantFiles = await findRelevantFilesFromInstructions({ instructions: debouncedInstructions, tokenBudget }, controller.signal);
        if (controller.signal.aborted) return;

        if (relevantFiles) {
          const filesSet = new Set(relevantFiles);
          setContextSelectedFiles(new Map([['auto-context', filesSet]]));
          setCachedInstructionContext(filesSet);
        } else {
          setContextSelectedFiles(new Map());
        }
      
      } else { // No files selected and no instructions
        setContextSelectedFiles(new Map());
        useWorkspaceStore.setState({ lastRelevantFileQuery: '' }); // Reset to allow future queries
      }
    }
    
    recomputeContext();

    return () => {
      controller.abort();
    }
  }, [isStateLoaded, userSelectedFiles, tokenBudget, isAutoContextEnabled, findRelevantFiles, setContextSelectedFiles, debouncedInstructions, findRelevantFilesFromInstructions, cachedInstructionContext, setCachedInstructionContext, fileTree]);

  const combinedSelectedFiles = useMemo(() => {
    const all = new Set(userSelectedFiles);
    contextSelectedFiles.forEach((files) => {
      files.forEach((file) => all.add(file));
    });
    return all;
  }, [userSelectedFiles, contextSelectedFiles]);

  const nodeSelectionStates = useMemo(() => {
    const states = new Map<string, 'checked' | 'unchecked' | 'indeterminate'>();
    const tokenizableDescendants = new Map<string, boolean>();
    const folderSelectionTypes = new Map<string, 'user' | 'context' | 'mixed' | 'none'>();

    const contextOnlyFiles = new Set<string>();
    contextSelectedFiles.forEach(files => {
      files.forEach(file => contextOnlyFiles.add(file));
    });

    interface NodeCounts {
      totalSelectable: number;
      selectedSelectable: number;
      selectedIgnored: number;
      userSelected: number;
      contextOnlySelected: number;
    }

    function getCounts(node: FileNode): NodeCounts {
      if (node.type === 'file') {
        const isTokenizable = !!node.tokenizable;
        tokenizableDescendants.set(node.path, isTokenizable);
        if (!isTokenizable) return { totalSelectable: 0, selectedSelectable: 0, selectedIgnored: 0, userSelected: 0, contextOnlySelected: 0 };

        const isUser = userSelectedFiles.has(node.path);
        const isContext = contextSelectedFiles.has(node.path) || Array.from(contextSelectedFiles.values()).some(s => s.has(node.path));
        const isContextOnly = isContext && !isUser;

        if (node.ignored) {
          return {
            totalSelectable: 0,
            selectedSelectable: 0,
            selectedIgnored: (isUser || isContext) ? 1 : 0,
            userSelected: isUser ? 1 : 0,
            contextOnlySelected: isContextOnly ? 1 : 0,
          };
        } else {
          return {
            totalSelectable: 1,
            selectedSelectable: (isUser || isContext) ? 1 : 0,
            selectedIgnored: 0,
            userSelected: isUser ? 1 : 0,
            contextOnlySelected: isContextOnly ? 1 : 0,
          };
        }
      }

      if (node.type === 'directory') {
        const totals: NodeCounts = { totalSelectable: 0, selectedSelectable: 0, selectedIgnored: 0, userSelected: 0, contextOnlySelected: 0 };

        if (node.children?.length) {
          for (const child of node.children) {
            const childCounts = getCounts(child);
            totals.totalSelectable += childCounts.totalSelectable;
            totals.selectedSelectable += childCounts.selectedSelectable;
            totals.selectedIgnored += childCounts.selectedIgnored;
            totals.userSelected += childCounts.userSelected;
            totals.contextOnlySelected += childCounts.contextOnlySelected;
          }
        }

        const hasTokenizable = totals.totalSelectable > 0 || totals.selectedIgnored > 0;
        tokenizableDescendants.set(node.path, hasTokenizable);

        let state: 'checked' | 'unchecked' | 'indeterminate' = 'unchecked';
        if (hasTokenizable) {
          if ((totals.selectedSelectable + totals.selectedIgnored) === 0) {
            state = 'unchecked';
          } else if (totals.selectedSelectable === totals.totalSelectable && totals.selectedIgnored === 0 && totals.totalSelectable > 0) {
            state = 'checked';
          }
          else state = 'indeterminate';
        }
        states.set(node.path, state);

        let folderType: 'user' | 'context' | 'mixed' | 'none' = 'none';
        if (totals.userSelected > 0 && totals.contextOnlySelected > 0) {
          folderType = 'mixed';
        } else if (totals.userSelected > 0) {
          folderType = 'user';
        } else if (totals.contextOnlySelected > 0) {
          folderType = 'context';
        }
        folderSelectionTypes.set(node.path, folderType);

        return totals;
      }
      return { totalSelectable: 0, selectedSelectable: 0, selectedIgnored: 0, userSelected: 0, contextOnlySelected: 0 };
    }
    
    fileTree.forEach(getCounts);
    return { nodeSelectionStates: states, hasTokenizableDescendants: tokenizableDescendants, folderSelectionTypes };
  }, [userSelectedFiles, contextSelectedFiles, fileTree]);

  const handleSelectAll = () => {
    const allFiles = new Set<string>();
    fileTree.forEach((node) => getAllDescendantFiles(node, allFiles));
    if (userSelectedFiles.size === allFiles.size) {
      setUserSelectedFiles(() => new Set());
    } else {
      setUserSelectedFiles(() => allFiles);
    }
  };

  const areAllFilesSelected = useMemo(() => {
    const allFiles = new Set<string>();
    fileTree.forEach((node) => getAllDescendantFiles(node, allFiles));
    return allFiles.size > 0 && allFiles.size === combinedSelectedFiles.size;
  }, [combinedSelectedFiles, fileTree, getAllDescendantFiles]);

  return {
    toggleNodeSelection,
    combinedSelectedFiles,
    nodeSelectionStates: nodeSelectionStates.nodeSelectionStates,
    hasTokenizableDescendants: nodeSelectionStates.hasTokenizableDescendants,
    folderSelectionTypes: nodeSelectionStates.folderSelectionTypes,
    handleSelectAll,
    areAllFilesSelected,
    getAllDescendantFiles,
  };
};